import { defineStore } from "pinia";
import { ShopLight } from "@/abilities/shop/types/shop-light";
import { Priority, SendMailsInput, useUserQuery } from "@/shared/services/graphql/generated/consumer-graph-types";
import { get as _get, set as _set } from "lodash";
import { computed, ref, watch } from "vue";
import { getUiLanguage } from "@/shared/services/providers/language-provider";

export const useCheckoutStore = defineStore("shopLightCheckout", () => {
    const syncShippingAndBillingAddress = ref(false);
    const acceptedAGB = ref(false);

    const { result: user } = useUserQuery();

    const basketRequestInfos = ref<ShopLight.Checkout.BasketRequestInfos>({
        contactData: {
            additionalEmail: "",
            contactEmail: "",
            firstName: "",
            familyName: "",
            position: "",
            company: "",
        },
        requestDetails: { subject: "", remark: "", priority: "standard" },
        billingAddress: {
            company: "",
            forAttentionOf: "",
            street: "",
            houseNumber: "",
            addressSupplement: "",
            city: "",
            postalCode: "",
            country: "",
        },
        shippingAddress: {
            company: "",
            forAttentionOf: "",
            street: "",
            houseNumber: "",
            addressSupplement: "",
            city: "",
            postalCode: "",
            country: "",
        },
    });

    function setAcceptedAGB(accepted: boolean) {
        acceptedAGB.value = accepted;
    }

    function updateBasketRequestInfos(fieldPath: string, value: string) {
        _set(basketRequestInfos.value, fieldPath, value);
    }

    function getBasketRequestInfoValue(fieldPath: string): string {
        return _get(basketRequestInfos.value, fieldPath);
    }

    function haveTabValuesChanged(tab: ShopLight.Checkout.TabContent): boolean {
        if (!tab.tabPage?.tabAreas) return false;
        const allInputs = tab.tabPage.tabAreas.flatMap((area) => area.inputs.flat());
        return allInputs.some((input) => {
            const value = getBasketRequestInfoValue(input.id);
            if (input.id === ShopLight.Checkout.BasketRequestInfoPaths.contactData.contactEmail) {
                return value !== user.value?.me.email;
            } else {
                return (
                    value !== undefined &&
                    value !== null &&
                    value !== "" &&
                    input.type !== "checkbox" &&
                    input.type !== "dropdown"
                );
            }
        });
    }

    watch(
        () => basketRequestInfos.value.billingAddress,

        () => {
            if (syncShippingAndBillingAddress.value) {
                basketRequestInfos.value.shippingAddress = { ...basketRequestInfos.value.billingAddress };
            }
        },
        { deep: true, immediate: true }
    );

    watch(
        user,
        () => {
            if (user.value?.me.email) {
                updateBasketRequestInfos(
                    ShopLight.Checkout.BasketRequestInfoPaths.contactData.contactEmail,
                    user.value.me.email
                );
            }
        },
        { immediate: true }
    );

    function setSyncShippingAddress(sync: boolean) {
        syncShippingAndBillingAddress.value = sync;
        if (sync) basketRequestInfos.value.shippingAddress = { ...basketRequestInfos.value.billingAddress };
    }

    const basketRequestForMail = computed<SendMailsInput>(() => {
        const { contactData, requestDetails, billingAddress, shippingAddress } = basketRequestInfos.value;
        return {
            billingAddress: {
                attnOfName: billingAddress.forAttentionOf,
                cityCode: billingAddress.postalCode,
                cityName: billingAddress.city,
                companyName: billingAddress.company,
                country: billingAddress.country,
                extraInfo: billingAddress.addressSupplement,
                houseNumber: billingAddress.houseNumber,
                street: billingAddress.street,
            },
            shippingAddress: {
                attnOfName: shippingAddress.forAttentionOf,
                cityCode: shippingAddress.postalCode,
                cityName: shippingAddress.city,
                companyName: shippingAddress.company,
                country: shippingAddress.country,
                extraInfo: shippingAddress.addressSupplement,
                houseNumber: shippingAddress.houseNumber,
                street: shippingAddress.street,
            },
            contact: {
                companyName: contactData.company,
                emailCc: contactData.additionalEmail.split(","),
                emailTo: contactData.contactEmail,
                firstName: contactData.firstName,
                jobTitle: contactData.position,
                lastName: contactData.familyName,
            },
            mailData: {
                // until now, only mail support in the languages de or en. fallback en if uilanguage is not de
                language: getUiLanguage() === "de" ? "de" : "en",
                priority: requestDetails.priority as Priority,
                subject: requestDetails.subject,
                request: requestDetails.remark,
            },
        };
    });

    return {
        //via state or via getter: all state properties must be returned, otherwise pinia will not track them
        //getter
        acceptedAGB: computed(() => acceptedAGB.value),
        basketRequestForMail,
        syncShippingAndBillingAddress: computed(() => syncShippingAndBillingAddress.value),
        getBasketRequestInfoValue,

        //actions
        haveTabValuesChanged,
        setSyncShippingAddress,
        setAcceptedAGB,
        updateBasketRequestInfos,
    };
});

export type CheckoutStore = ReturnType<typeof useCheckoutStore>;
